import { useEffect, useState } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Wind, CheckCircle2, PackageCheck, Loader2 } from "lucide-react";
import { MaskedLine } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(location.state?.order || null);
  const [status, setStatus] = useState(location.state?.order ? "paid" : sessionId ? "polling" : "none");

  useEffect(() => {
    if (!sessionId || order) return;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order || null);
          setStatus("paid");
          clearInterval(timer);
        } else if (attempts >= 15) {
          setStatus("timeout");
          clearInterval(timer);
        }
      } catch {
        if (attempts >= 15) {
          setStatus("timeout");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, order]);

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-ink" data-testid="order-success-page">
      <div className="spotlight absolute inset-0" aria-hidden="true" />
      <header className="relative z-10 border-b border-steel">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest">
            <Wind className="h-5 w-5 text-volt" strokeWidth={2.5} />
            <span>X8<span className="text-volt">/</span>Violent Fan</span>
          </Link>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Order status</span>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-1 flex-col justify-center px-5 py-20 md:px-10">
        {status === "polling" && (
          <div className="flex items-center gap-4 font-mono text-sm text-smoke" data-testid="order-polling">
            <Loader2 className="h-5 w-5 animate-spin text-volt" /> Confirming your payment…
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="order-timeout">
            <h1 className="font-display text-3xl font-black uppercase md:text-5xl">Payment received</h1>
            <p className="mt-4 max-w-lg text-smoke">We're still confirming a few details — your confirmation email will arrive shortly. Questions? Reply to your receipt email.</p>
          </div>
        )}

        {status === "paid" && (
          <>
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-smoke">
              <CheckCircle2 className="h-5 w-5 text-volt" /> Payment received
            </div>
            <h1 className="mt-8 font-display font-black uppercase leading-[0.9] tracking-tighter">
              <MaskedLine delay={0.1} className="text-[12vw] md:text-[7vw]">You're in.</MaskedLine>
              <MaskedLine delay={0.25} className="text-[12vw] md:text-[7vw]">
                <span className="text-volt">The X8</span> <span className="text-outline">is yours.</span>
              </MaskedLine>
            </h1>
            {order && (
              <div className="mt-10 max-w-xl border border-steel bg-carbon p-6" data-testid="order-details">
                <div className="grid gap-2 font-mono text-xs">
                  <div className="flex justify-between"><span className="text-smoke">ORDER</span><span className="font-bold text-volt" data-testid="success-order-id">{order.id}</span></div>
                  <div className="flex justify-between"><span className="text-smoke">BUNDLE</span><span>{order.bundle_label}</span></div>
                  <div className="flex justify-between"><span className="text-smoke">TOTAL</span><span>${order.amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-smoke">RECEIPT</span><span>{order.customer_email}</span></div>
                </div>
              </div>
            )}
            <p className="mt-6 flex max-w-xl items-start gap-3 text-sm leading-relaxed text-smoke md:text-base">
              <PackageCheck className="mt-1 h-5 w-5 shrink-0 text-volt" />
              A confirmation email is on its way. Your X8 ships within 24–48 hours with tracking.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/account" data-testid="track-order-link" className="border border-volt bg-volt px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] uppercase text-ink transition-colors duration-200 hover:bg-transparent hover:text-volt">
                Track in my account
              </Link>
              <Link to="/" data-testid="back-home-link" className="border border-steel px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-200 hover:border-volt hover:text-volt">
                Back to store
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
